import { useParams, Link, Navigate } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { frameworks, getFrameworkBySlug } from '@/content/compliance';
import { NotifyForm } from '@/components/NotifyForm';
import { useSeo } from '@/seo/useSeo';

export function ComplianceDetail(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const framework = getFrameworkBySlug(slug);

  useSeo({
    title: framework ? `${framework.name} for MSPs` : 'Framework not found',
    description: framework ? framework.summary : 'This compliance page could not be found.',
    path: `/compliance/${slug ?? ''}`,
  });

  if (!framework) return <Navigate to="/compliance" replace />;

  const Icon = framework.icon;
  const related = frameworks.filter((f) => f.slug !== framework.slug).slice(0, 3);

  return (
    <div>
      <section className="relative overflow-hidden pt-16 pb-16 md:pt-24 md:pb-24">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-nexus-grid bg-grid [mask-image:radial-gradient(circle_at_50%_0%,#000,transparent_70%)]"
        />
        <div className="container-content relative max-w-[760px]">
          <p className="text-xs text-white/40">
            <Link to="/compliance" className="text-white/40 no-underline hover:text-nexus-purple-light">
              Compliance
            </Link>{' '}
            / {framework.name}
          </p>
          <div className="mb-5 mt-6 flex h-12 w-12 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
            <Icon fontSize="medium" />
          </div>
          <h1 className="font-display text-4xl font-bold leading-[1.1] tracking-tight text-nexus-ink md:text-[44px]">
            {framework.fullName} for MSPs
          </h1>
          <p className="mt-5 text-lg text-white/70">{framework.summary}</p>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content max-w-[760px]">
          <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">
            Why this reaches an MSP, not just the client
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white/75">{framework.whyItReachesYou}</p>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">
            What the Nexus compliance module tracks
          </h2>
          <ul className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
            {framework.whatNexusTracks.map((c) => (
              <li key={c} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <CheckCircleOutlineIcon fontSize="small" className="mt-0.5 flex-shrink-0 text-nexus-purple-light" />
                <span className="text-sm text-white/75">{c}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs text-white/40">
            This describes what the module tracks against {framework.name} — not a claim that Nexus
            or any client on it is certified or audited against it. See the{' '}
            <Link to="/platform/compliance-qbr" className="text-white/50 underline hover:text-white/70">
              compliance &amp; QBR module
            </Link>{' '}
            for the full picture, or read the{' '}
            <Link
              to={framework.relatedBlogSlug ? `/blog/${framework.relatedBlogSlug}` : '/blog'}
              className="text-white/50 underline hover:text-white/70"
            >
              blog
            </Link>{' '}
            for more on how we think about compliance tooling.
          </p>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">Other frameworks</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {related.map((f) => {
              const RelIcon = f.icon;
              return (
                <Link
                  key={f.slug}
                  to={`/compliance/${f.slug}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 no-underline transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50"
                >
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
                    <RelIcon fontSize="small" />
                  </div>
                  <h3 className="font-display text-[15px] font-semibold text-nexus-ink">{f.name}</h3>
                  <p className="mt-1.5 text-sm text-white/65">{f.summary}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
            <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
              Ready to see {framework.name} posture tracked for real?
            </h2>
            <p className="mx-auto mt-3 max-w-[520px] text-white/70">
              Join the design-partner cohort and we&apos;ll walk through the control mapping for your
              own clients.
            </p>
            <div className="mt-8 flex justify-center">
              <NotifyForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
